import { change, getFormValues, InjectedFormProps, reduxForm } from "redux-form";
import { every, size } from "lodash";
import { useMemo } from "react";

import { Button, Column, Row, Separator, TextDisplay, Wrapper } from "src/styled";
import { Input, IntlPhoneNumberInput, TypedField } from "src/components";
import { isEmail, isIntlPhone, isRequired } from "src/utils/validators";
import { SchedulerWizardFormValues } from "src/interfaces";
import { translate } from "src/services";
import { useAppDispatch, useAppSelector } from "src/redux/store";
import { WIZARD_FORM_NAME } from "./AddressForm";

interface PropsWithoutReduxForm {
  goToPreviousStep: () => void;
  isSummary: boolean;
  onCancelUpdateContactInformation: () => void;
}

type Props = PropsWithoutReduxForm &
  InjectedFormProps<SchedulerWizardFormValues, PropsWithoutReduxForm>;

const ContactInfoForm = ({
  handleSubmit,
  goToPreviousStep,
  isSummary,
  onCancelUpdateContactInformation,
}: Props) => {
  const dispatch = useAppDispatch();
  const {
    isSaving,
    isUpdatingContactInfo,
    scheduledPickupData: { email, firstName, lastName, phoneNumber },
    errors: { addressValidation, maxItemsPerRequestReached },
  } = useAppSelector((state) => state.bulkyItemsScheduler);

  const thisFormValues = useAppSelector(
    getFormValues(WIZARD_FORM_NAME)
  ) as SchedulerWizardFormValues;

  const isSubmitDisabled = useMemo(() => {
    const hasItems = size(thisFormValues?.items) > 0;

    const eachItemHasDate = every(thisFormValues?.items, (item) => !!item.pickupDate);

    if (isSummary) {
      // if any of the fields are different from the initial values, enable the submit button
      return (
        isUpdatingContactInfo ||
        (thisFormValues?.email === email &&
          thisFormValues?.firstName === firstName &&
          thisFormValues?.lastName === lastName &&
          thisFormValues?.phoneNumber === phoneNumber)
      );
    }

    return (
      isSaving || addressValidation || maxItemsPerRequestReached || !hasItems || !eachItemHasDate
    );
  }, [
    thisFormValues?.items,
    thisFormValues?.email,
    thisFormValues?.firstName,
    thisFormValues?.lastName,
    thisFormValues?.phoneNumber,
    isSummary,
    isSaving,
    addressValidation,
    maxItemsPerRequestReached,
    isUpdatingContactInfo,
    email,
    firstName,
    lastName,
    phoneNumber,
  ]);

  const cancelUpdateContactInformation = () => {
    dispatch(change(WIZARD_FORM_NAME, "firstName", firstName));
    dispatch(change(WIZARD_FORM_NAME, "lastName", lastName));
    dispatch(change(WIZARD_FORM_NAME, "email", email));
    dispatch(change(WIZARD_FORM_NAME, "phoneNumber", phoneNumber));

    onCancelUpdateContactInformation();
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <Wrapper padding="0 50px" maxWidth={500} margin="no auto">
        <Separator height={50} />
        <TextDisplay margin="no no medium" block>
          <b>
            {isSummary
              ? translate("wizardPage.updateContactInformation")
              : translate("wizardPage.contactInformation")}
          </b>
        </TextDisplay>
        <Row>
          <Column md={6} padding="no small">
            <TypedField
              name="firstName"
              component={Input}
              props={{
                type: "text",
                label: translate("common.firstName"),
              }}
              validate={[isRequired]}
            />
          </Column>
          <Column md={6} padding="no small">
            <TypedField
              name="lastName"
              component={Input}
              props={{
                type: "text",
                label: translate("common.lastName"),
              }}
              validate={[isRequired]}
            />
          </Column>
        </Row>

        <TypedField
          name="email"
          component={Input}
          props={{
            type: "email",
            label: translate("common.emailAddress"),
          }}
          validate={[isRequired, isEmail]}
        />
        <TypedField
          component={IntlPhoneNumberInput}
          name="phoneNumber"
          props={{
            initialValue: thisFormValues?.phoneNumber || "",
            changeFieldValue: (value: string) => {
              dispatch(change(WIZARD_FORM_NAME, "phoneNumber", value));
            },
            margin: "no auto",
            label: translate("common.phoneNumber"),
          }}
          validate={[isIntlPhone]}
        />
        {!isSummary && (
          <TextDisplay margin="medium no" size="small" block>
            {translate("wizardPage.contactInfoNote")}
          </TextDisplay>
        )}
        <Separator height={50} />
        {isSummary ? (
          <>
            <Wrapper display="flex" justifyContent="center">
              <Button
                type="submit"
                color="primary"
                size="small"
                borderRadius="100px"
                margin="no xSmall no no"
                disabled={isSubmitDisabled}
              >
                {translate("wizardPage.updateInformation")}
              </Button>

              <Button
                type="button"
                color="secondary"
                size="small"
                borderRadius="100px"
                margin="no xSmall no no"
                onClick={cancelUpdateContactInformation}
              >
                {translate("common.cancel")}
              </Button>
            </Wrapper>
          </>
        ) : (
          <Wrapper display="flex" justifyContent="space-between">
            <Button
              type="button"
              color="primary"
              size="small"
              borderRadius="100px 0 0 100px"
              margin="no xSmall no no"
              line
              disabled={isSaving}
              onClick={goToPreviousStep}
            >
              {translate("common.previousStep")}
            </Button>
            <Button
              type="submit"
              color="primary"
              size="medium"
              borderRadius="0 100px 100px 0"
              disabled={isSubmitDisabled}
              loadingIndicator={isSaving}
            >
              {translate("common.submitRequest")}
            </Button>
          </Wrapper>
        )}

        <Separator height={100} />
      </Wrapper>
    </form>
  );
};

export default reduxForm<SchedulerWizardFormValues, PropsWithoutReduxForm>({
  form: WIZARD_FORM_NAME,
  destroyOnUnmount: false,
  forceUnregisterOnUnmount: true,
})(ContactInfoForm);
